import { useState, useRef, useEffect } from 'react';
import { useCompany } from '../contexts/CompanyContext';

function truncate(str: string, max: number) {
  return str.length > max ? str.slice(0, max) + '…' : str;
}

interface CompanySwitcherProps {
  onAddCompany: () => void;
}

export default function CompanySwitcher({ onAddCompany }: CompanySwitcherProps) {
  const { entrepriseId, entreprises, setActiveCompany } = useCompany();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = entreprises.find((e) => e.id === entrepriseId);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full text-left px-5 pb-4 mb-2 border-b border-slate-700"
        aria-label={current?.raison_sociale || 'Aucune société'}
      >
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="text-lg font-bold text-white tracking-tight truncate max-w-[130px]">
            {current ? current.raison_sociale : 'Aucune société'}
          </span>
          {current?.forme_juridique && (
            <span className="text-xs text-slate-400 font-normal flex-shrink-0">{current.forme_juridique}</span>
          )}
        </span>
        <span className="text-slate-400 text-xs ml-auto flex-shrink-0">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="absolute left-2 right-2 top-full bg-slate-800 border border-slate-600 rounded-lg shadow-lg z-50 overflow-hidden">
          {entreprises.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-400">Aucune société</div>
          )}
          {entreprises.map((e) => (
            <button
              key={e.id}
              onClick={() => {
                setActiveCompany(e.id, e.exercices?.[0]?.id || '');
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-700 flex items-center gap-2 transition-colors"
            >
              {e.id === entrepriseId && (
                <span className="text-green-400 text-xs">&#10003;</span>
              )}
              <span className={e.id === entrepriseId ? 'text-white font-medium' : 'text-slate-300'}>
                {truncate(e.raison_sociale, 18)}
              </span>
            </button>
          ))}
          <button
            onClick={() => {
              onAddCompany();
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2.5 text-sm text-amber-400 hover:bg-slate-700 border-t border-slate-600 transition-colors"
          >
            + Nouvelle société
          </button>
        </div>
      )}
    </div>
  );
}
